import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { Menu, X, User, LogIn } from 'lucide-react'
import { useState, useMemo, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useWallet } from '@solana/wallet-adapter-react'
import { useTranslation } from 'react-i18next'
import { api } from '../../services/api'
import AuthModal from '../auth/AuthModal'
import { PhantomButton, PhantomButtonCompact } from '../wallet'
import { LanguageToggle } from '../LanguageToggle'
import { useUser } from '../../contexts/UserContext'

/**
 * BTC Rate Display Component
 * Shows current BTC/USDT rate in the header
 */
function BtcRateDisplay() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['btcPrice'],
    queryFn: () => api.getBtcPrice(),
    refetchInterval: 60000, // Refetch every 60 seconds
    retry: 2,
    staleTime: 30000, // Consider data stale after 30 seconds
  })

  // Don't break header if price fetch fails
  if (error || !data?.data) {
    return null
  }

  const price = data.data.price
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)

  return (
    <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-900/10 border border-green-900/20">
      {isLoading ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">BTC</span>
          <div className="h-4 w-16 bg-gray-700/50 rounded animate-pulse" />
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gold-400">₿</span>
          <span className="text-sm font-semibold text-gray-200">{formattedPrice}</span>
        </div>
      )}
    </div>
  )
}

export default function Header() {
  const { t } = useTranslation(['navigation', 'common'])
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const queryClient = useQueryClient()
  const { disconnect: disconnectPhantom } = useWallet()
  const { user: contextUser, isAuthenticated: contextIsAuthenticated, tokenValidated } = useUser()

  const baseNavigation = useMemo(() => [
    { name: t('navigation:home'), href: '/app' },
    { name: t('navigation:vaults'), href: '/app/vaults' },
    { name: t('navigation:dashboard'), href: '/app/dashboard' },
    { name: t('navigation:portfolio'), href: '/app/portfolio' },
    { name: t('navigation:marketplace'), href: '/app/marketplace' },
  ], [t])

  // Use context authentication state (validated on mount)
  const isAuthenticated = tokenValidated ? contextIsAuthenticated : api.isAuthenticated()
  
  // Auto-open login modal when visiting with referral code and not authenticated
  useEffect(() => {
    const refCode = searchParams.get('ref')
    if (refCode && !isAuthenticated) {
      // Small delay to ensure the page has rendered
      const timer = setTimeout(() => {
        setAuthModalOpen(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [searchParams, isAuthenticated])

  // Listen for invalid token event (user deleted but token exists)
  useEffect(() => {
    const handleShowRegisterModal = (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('Received show-register-modal event:', customEvent.detail)
      setAuthModalOpen(true)
    }

    window.addEventListener('show-register-modal', handleShowRegisterModal)
    return () => window.removeEventListener('show-register-modal', handleShowRegisterModal)
  }, [])

  // v3.0: Check if user is a linked partner
  const isPartner = api.isPartner()

  const { data: currentUserResponse } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.getCurrentUser(),
    enabled: isAuthenticated && !contextUser, // Only fetch if context doesn't have user
    retry: false,
  })

  // Use user from context (validated on mount) or from query (legacy support)
  const currentUser = contextUser || currentUserResponse?.data

  // v3.0: Dynamic navigation - add Partner Dashboard for linked partners
  const navigation = useMemo(() => {
    if (isPartner && isAuthenticated) {
      return [
        ...baseNavigation,
        { name: t('navigation:partnerDashboard'), href: '/referrer/dashboard' },
      ]
    }
    return baseNavigation
  }, [isPartner, isAuthenticated, baseNavigation, t])

  const handleLogout = async () => {
    // Logout from backend (clear JWT token)
    api.logout()

    // Disconnect Phantom wallet via adapter
    try {
      await disconnectPhantom()
    } catch (error) {
      console.log('Phantom already disconnected')
    }

    // Clear all cached data
    queryClient.clear()

    // Redirect to home
    window.location.href = '/app'
  }

  return (
    <header className="sticky top-0 z-50 border-b border-green-900/20 bg-background-primary/95 backdrop-blur supports-[backdrop-filter]:bg-background-primary/80">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo and BTC Rate */}
          <div className="flex items-center gap-4">
            <Link to="/app" className="flex items-center">
              <img
                src="/images/brand/logo-takara.png"
                alt="Takara"
                className="h-8 sm:h-10 w-auto"
              />
            </Link>
            {/* BTC Rate Display */}
            <BtcRateDisplay />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-green-900/20 text-gold-500'
                      : 'text-gray-300 hover:bg-green-900/10 hover:text-gold-400'
                  }`}
                >
                  {item.name}
                </Link>
              )
            })}
          </div>

          {/* Language Toggle, Wallet Button & Profile */}
          <div className="hidden md:flex md:items-center md:gap-3">
            {/* Language Toggle */}
            <LanguageToggle />

            {/* Password Auth - Login/Logout Button */}
            {isAuthenticated && currentUser ? (
              <>
                <Link
                  to="/app/profile"
                  className={`p-2.5 rounded-lg transition-colors ${
                    location.pathname === '/app/profile'
                      ? 'bg-green-900/20 text-gold-500'
                      : 'text-gray-300 hover:bg-green-900/10 hover:text-gold-400'
                  }`}
                  title={`Logged in as ${currentUser.username || currentUser.email || 'User'}`}
                >
                  <User className="h-5 w-5" />
                </Link>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2.5 rounded-lg font-semibold bg-gray-700 text-gray-300 hover:opacity-90 transition-opacity"
                >
                  {t('common:buttons.logout')}
                </button>
              </>
            ) : (
              <button
                onClick={() => setAuthModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-green-600 text-white hover:opacity-90 transition-opacity"
              >
                <LogIn className="h-4 w-4" />
                <span className="hidden lg:inline">{t('common:buttons.login')}</span>
              </button>
            )}

            {/* Wallet Button - Only show if authenticated */}
            {isAuthenticated && (
              <PhantomButton />
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-green-900/10 hover:text-gold-400"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-4 py-2 rounded-lg text-base font-medium transition-colors ${
                    isActive
                      ? 'bg-green-900/20 text-gold-500'
                      : 'text-gray-300 hover:bg-green-900/10 hover:text-gold-400'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )
            })}
            {isAuthenticated && (
              <Link
                to="/app/profile"
                className={`block px-4 py-2 rounded-lg text-base font-medium transition-colors ${
                  location.pathname === '/app/profile'
                    ? 'bg-green-900/20 text-gold-500'
                    : 'text-gray-300 hover:bg-green-900/10 hover:text-gold-400'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('navigation:profile')}
              </Link>
            )}

            {/* Language Toggle for mobile */}
            <div className="px-2 py-2">
              <LanguageToggle />
            </div>

            {/* Login Button for mobile */}
            {!isAuthenticated && (
              <button
                onClick={() => {
                  setAuthModalOpen(true)
                  setMobileMenuOpen(false)
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-green-600 text-white hover:opacity-90 transition-opacity"
              >
                <LogIn className="h-4 w-4" />
                <span>{t('common:buttons.login')}</span>
              </button>
            )}

            {/* Wallet Button - Only show if authenticated */}
            {isAuthenticated && (
              <div className="pt-2 space-y-2">
                <PhantomButtonCompact className="w-full" />

                {/* Logout Button for mobile */}
                {currentUser && (
                  <button
                    onClick={() => {
                      handleLogout()
                      setMobileMenuOpen(false)
                    }}
                    className="w-full px-4 py-2.5 rounded-lg font-semibold bg-gray-700 text-gray-300 hover:opacity-90 transition-opacity"
                  >
                    {t('common:buttons.logout')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['currentUser'] })
        }}
      />
    </header>
  )
}
